import { Close, FiberManualRecord } from '@mui/icons-material'
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'

import { ProviderTaskHistory } from '../../../providers/ProviderTask/ProviderTask.provider'
import { themeColors } from '../../../theme'

interface TaskHistoryTimelineProps {
  open: boolean
  onClose: () => void
  taskId: number | null
  providerTaskHistory: ProviderTaskHistory[] | null
  onFetchProviderTaskHistory: (providerTaskId: number) => Promise<any>
}

const fieldColorMap: Record<string, string> = {
  Status: themeColors.blue,
  Barrier: themeColors.orange,
  Priority: themeColors.yellow,
  'Task Type': themeColors.teal50,
  'Assigned To': themeColors.darkPurple,
  'Due Date': themeColors.green,
  Deleted: themeColors.red,
}

export const TaskHistoryTimeline = ({
  open,
  onClose,
  taskId,
  providerTaskHistory,
  onFetchProviderTaskHistory,
}: TaskHistoryTimelineProps) => {
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (open && taskId) {
      setLoading(true)
      onFetchProviderTaskHistory(taskId)
      setLoading(false)
    }
  }, [open, taskId])

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: themeColors.darkPurple,
          color: themeColors.white,
        }}
      >
        <Typography fontWeight={600} fontSize={20}>
          Task #{taskId} History
        </Typography>
        <IconButton onClick={onClose} size='small'>
          <Close sx={{ color: themeColors.white }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ py: 3, px: 4 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : providerTaskHistory?.length === 0 ? (
          <Typography color='textSecondary' sx={{ py: 4, textAlign: 'center' }}>
            No history found for this task.
          </Typography>
        ) : (
          <Box sx={{ position: 'relative', mt: 2 }}>
            {/* Vertical line */}
            <Box
              sx={{
                position: 'absolute',
                left: 8,
                top: 0,
                bottom: 0,
                width: 2,
                backgroundColor: themeColors.gray20,
              }}
            />

            {providerTaskHistory?.map((entry, index) => {
              const color =
                fieldColorMap[entry.fieldChanged] ?? themeColors.gray20
              const isFirst = entry.oldValue === ''

              return (
                <Box
                  key={entry.id}
                  sx={{
                    display: 'flex',
                    gap: 2,
                    mb: index < providerTaskHistory.length - 1 ? 3 : 0,
                    position: 'relative',
                  }}
                >
                  {/* Dot */}
                  <FiberManualRecord
                    sx={{
                      fontSize: 18,
                      color,
                      zIndex: 1,
                      backgroundColor: '#fff',
                    }}
                  />

                  {/* Content */}
                  <Box sx={{ flex: 1 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        mb: 0.5,
                      }}
                    >
                      <Typography fontWeight={600} fontSize={14} sx={{ color }}>
                        {entry.fieldChanged}
                      </Typography>
                      <Typography fontSize={12} color='textSecondary'>
                        {dayjs(entry.changedAt).format('MM/DD/YYYY h:mm A')}
                      </Typography>
                    </Box>

                    {isFirst ? (
                      <Typography fontSize={14}>
                        Set to{' '}
                        <Box component='span' fontWeight={600}>
                          {entry.newValue}
                        </Box>
                      </Typography>
                    ) : (
                      <Typography fontSize={14}>
                        <Box component='span' fontWeight={600}>
                          {entry.oldValue}
                        </Box>
                        {' → '}
                        <Box component='span' fontWeight={600}>
                          {entry.newValue}
                        </Box>
                      </Typography>
                    )}

                    <Typography
                      fontSize={12}
                      color='textSecondary'
                      sx={{ mt: 0.25 }}
                    >
                      by {entry.changedByProviderName}
                    </Typography>
                  </Box>
                </Box>
              )
            })}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  )
}
